"use client";
import Link from "next/link";
import React, { useState, useEffect } from "react";
import {
  User,
  Phone,
  Car,
  MapPin,
  Calendar,
  Clock,
  Send,
  AlertCircle,
  CheckCircle2,
  Wrench,
  Building2,
  Home,
  Loader2,
} from "lucide-react";
import Swal from "sweetalert2";
import dynamic from "next/dynamic";

const MapPicker = dynamic(() => import("./MapPicker"), { ssr: false });

// ─── Hardcoded data provided by backend ───────────────────────────────────────
const HARDCODED_PROVINCE = {
  Code: 20,
  UniqueId: "fc74bc7a-a694-46a1-816d-7838bd5c3d1f",
  SubCountryLocalizedName: "تهران",
  SubCountryEnglishName: null,
  CountryName: "ایران",
  SubCountryDisplayInOnlineSales: false,
  ClientId: "0602ccde-4013-48fe-93a6-a8749feb08c5",
  IsDirty: false,
};

const HARDCODED_CITY = {
  Code: 31,
  UniqueId: "29e80ca5-1d56-4509-9218-134cd8b3c6fc",
  CityLocalizedName: "تهران",
  CityEnglishName: null,
  SubCountryName: "تهران",
  CountryName: "ایران",
  SubCountryId: "fc74bc7a-a694-46a1-816d-7838bd5c3d1f",
  DepivedArea: false,
  DisplayInOnlineSales: false,
  CityCategoryId: null,
  CityCategoryLocalizedName: null,
  ClientId: "0255c9aa-0c14-4b74-88f9-c18bdde04e13",
  IsDirty: false,
};

const HARDCODED_DEALER = {
  Code: 1,
  UniqueId: "9afd956b-9155-4043-a5d0-1290fcf5ec99",
  DealerName: "برساوش تجارت نیک",
  DealerNo: "1001",
  City: "تهران",
  IsActiveBooking: true,
  ClientId: "fb4456e3-e52d-44c0-a44c-6562ccbbe1e9",
  IsDirty: false,
};

const HARDCODED_BRANCH = {
  Code: 4,
  UniqueId: "346f9259-e50c-4d10-8154-3433488c95cd",
  DealerName: "برساوش تجارت نیک",
  BranchNo: "1002",
  BranchName: "ستاد مرکزی خدمات پس از فروش",
  ManagerName: "میرمحمدرضا پور موسوی",
  BranchGrade: "A",
  CityName: "تهران",
  BranchStatus: "Active",
  IsActiveBooking: false,
  ClientId: "607a9996-ce6a-48e1-be7b-9843bf3fe234",
  IsDirty: false,
};

const HARDCODED_SERVER_GROUP = {
  Code: 16,
  UniqueId: "b181b8b9-d477-4865-b6a3-f893de7abc5a",
  ServerGroupName: "Home Service",
  ServerGroupLocalizedName: "سرویس در محل",
  AllVehicleModel: true,
  Order: 16,
};

// ─── Helper ───────────────────────────────────────────────────────────────────
function verifyBzOffline(vin) {
  vin = (vin || "").toUpperCase().trim();
  if (vin.length !== 17 || /[IOQ]/.test(vin)) {
    return { match: false, reason: "Invalid VIN length or illegal characters" };
  }
  const wmi = vin.substring(0, 3);
  const vds = vin.substring(3, 8);

  const bz4x_global_vds = new Set(["AAAAA", "ABACA"]);
  const bz3_faw_vds = new Set(["AS14U"]);
  const bz4x_gac_vds = new Set(["D3135"]);
  const bz4x_faw_vds = new Set([]);

  if (wmi === "JTM" && bz4x_global_vds.has(vds)) {
    return { match: true, model: "Toyota bZ4X", market: "Global (Japan)" };
  } else if (wmi === "LFM" && bz3_faw_vds.has(vds)) {
    return { match: true, model: "Toyota bZ3", market: "China (FAW)" };
  } else if (wmi === "LVG" && bz4x_gac_vds.has(vds)) {
    return { match: true, model: "Toyota bZ4X", market: "China (GAC)" };
  } else if (wmi === "LFM" && bz4x_faw_vds.has(vds)) {
    return { match: true, model: "Toyota bZ4X", market: "China (FAW)" };
  }

  return { match: false, reason: "Valid VIN, but NOT a bZ3 or bZ4X" };
}

function normalizePersianDigits(str) {
  const persian = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
  const arabic = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
  let out = str;
  for (let i = 0; i < 10; i++) {
    out = out.replace(persian[i], i).replace(arabic[i], i);
  }
  return out;
}

// ─── Sub-components ──────────────────────────────────────────────────────────
function FormInput({ label, icon, name, value, onChange, placeholder, required, inputMode, maxLength, readOnly }) {
  return (
    <div className="flex flex-col gap-1 mt-10">
      <label htmlFor={name} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <span className="text-[#2b2b2b]">{icon}</span>
        {label}
        {required && <span className="text-red-500 text-xs">*</span>}
      </label>
      <input
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        inputMode={inputMode}
        maxLength={maxLength}
        readOnly={readOnly}
        dir="rtl"
        className={`w-full px-4 py-3 rounded-xl border-2 text-sm transition-all outline-none
          ${readOnly
            ? "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed"
            : "bg-white border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)]"
          }`}
      />
    </div>
  );
}

// InfoBadge and StepIndicator removed

function PlateInput({ formData, handleInputChange }) {
  const letters = ["ب", "ج", "د", "س", "ص", "ط", "ق", "ل", "م", "ن", "و", "ه", "ی"];
  return (
    <div className="flex flex-col gap-1 w-full relative mt-8">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1">
        <Car size={15} />
        پلاک خودرو
        <span className="text-red-500 text-xs">*</span>
      </label>
      <div dir="ltr" className="flex w-full border-2 border-slate-200 rounded-xl overflow-hidden bg-white items-stretch focus-within:border-blue-500 focus-within:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] transition-all h-[52px] text-sm sm:text-base">
        <div className="bg-blue-700 w-8 sm:w-10 flex flex-col items-center justify-center text-white font-bold text-[8px] sm:text-[10px] shrink-0 border-r border-slate-200">
          <span>I.R.</span>
          <span>IRAN</span>
        </div>
        <input type="text" name="platePart1" value={formData.platePart1} onChange={handleInputChange} className="flex-[1] min-w-0 text-center font-bold outline-none bg-transparent px-0.5" maxLength={2} placeholder="۱۲" inputMode="numeric" />
        <select name="plateLetter" value={formData.plateLetter} onChange={handleInputChange} className="flex-[1.2] min-w-0 text-center font-bold outline-none bg-slate-50 cursor-pointer border-x border-slate-100 appearance-none text-slate-700 px-0" dir="rtl">
          <option value="">--</option>
          {letters.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <input type="text" name="platePart2" value={formData.platePart2} onChange={handleInputChange} className="flex-[1.5] min-w-0 text-center font-bold outline-none bg-transparent px-0.5" maxLength={3} placeholder="۳۴۵" inputMode="numeric" />
        <div className="w-px bg-slate-300 my-2 shrink-0"></div>
        <div className="flex-[1.2] min-w-0 flex flex-col justify-center items-center relative px-0.5">
          <span className="text-[9px] text-slate-400 absolute top-0.5 inset-x-0 text-center">ایران</span>
          <input type="text" name="plateCityCode" value={formData.plateCityCode} onChange={handleInputChange} className="w-full text-center font-bold outline-none bg-transparent pt-3" maxLength={2} placeholder="۶۷" inputMode="numeric" />
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomeServiceForm() {
  const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "https://www.barsavosh.co/api/";
  const BOOKING_BASE = "/api/booking/";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVinValid, setIsVinValid] = useState(false);
  const [isValidatingVin, setIsValidatingVin] = useState(false);
  const [isFormRevealed, setIsFormRevealed] = useState(false);

  const [allocatedCars, setAllocatedCars] = useState([]);
  const [isFetchingCars, setIsFetchingCars] = useState(false);
  const [showManualVin, setShowManualVin] = useState(true);
  const [carsFetchedForNationalId, setCarsFetchedForNationalId] = useState("");

  // Form fields
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    vin: "",
    kilometer: "",
    platePart1: "",
    plateLetter: "",
    platePart2: "",
    plateCityCode: "",
    nationalCode: "",
    address: "",
    vehicleModelId: "",
  });

  // Date/Time selection
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [location, setLocation] = useState(null);

  // Dummy province selection to force user acknowledgment
  const [dummyProvince, setDummyProvince] = useState(null);

  // Fetch available dates on mount
  useEffect(() => {
    const fetchDates = async () => {
      setLoadingDates(true);
      try {
        const res = await fetch(
          `${BOOKING_BASE}Booking/getAllServerGroupDate?ServerGroupId=${HARDCODED_SERVER_GROUP.UniqueId}&BranchId=${HARDCODED_BRANCH.UniqueId}`
        );
        if (!res.ok) throw new Error("Failed to fetch dates");
        const data = await res.json();
        setAvailableDates(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Date fetch error:", err);
        setAvailableDates([]);
      } finally {
        setLoadingDates(false);
      }
    };
    fetchDates();
  }, []);

  // Fetch available times when a date is selected
  useEffect(() => {
    if (!selectedDate) return;
    const fetchTimes = async () => {
      setLoadingTimes(true);
      setAvailableTimes([]);
      setSelectedTime(null);
      try {
        const res = await fetch(
          `${BOOKING_BASE}Booking/getAllServerGroupTime?WorkShopTimeTableId=${selectedDate?.Value || selectedDate}&ServerGroupId=${HARDCODED_SERVER_GROUP.UniqueId}`
        );
        if (!res.ok) throw new Error("Failed to fetch times");
        const data = await res.json();
        setAvailableTimes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Time fetch error:", err);
        setAvailableTimes([]);
      } finally {
        setLoadingTimes(false);
      }
    };
    fetchTimes();
  }, [selectedDate]);

  const fetchCarsForNationalId = async (nationalId) => {
    if (!nationalId || nationalId.length !== 10) return;
    if (nationalId === carsFetchedForNationalId) return;

    setIsFetchingCars(true);
    setCarsFetchedForNationalId(nationalId);

    const QR_URL = process.env.NEXT_PUBLIC_API_QR_URL || 'https://api.barsavosh.co/api2/';
    const cleanQrUrl = QR_URL.endsWith('/') ? QR_URL : `${QR_URL}/`;

    try {
      const res = await fetch(`${cleanQrUrl}customers/external-cars/${nationalId}`, {
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.AllocatedCars && data.AllocatedCars.length > 0) {
          setAllocatedCars(data.AllocatedCars);
          setShowManualVin(false);
          setFormData((p) => ({ ...p, vin: "" })); // Reset vin when switching to dropdown
        } else {
          setAllocatedCars([]);
          setShowManualVin(true);
        }
      } else {
        setAllocatedCars([]);
        setShowManualVin(true);
      }
    } catch (e) {
      setAllocatedCars([]);
      setShowManualVin(true);
    } finally {
      setIsFetchingCars(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const normalized = normalizePersianDigits(value);

    if (name === "vin") {
      const finalVal = normalized.replace(/[^0-9a-zA-Z]/g, "").toUpperCase();
      setFormData((p) => ({ ...p, [name]: finalVal }));
      setIsVinValid(false);
      setIsFormRevealed(false);
    } else if (name === "vinSelect") {
      if (value === "manual") {
        setShowManualVin(true);
        setFormData((p) => ({ ...p, vin: "" }));
        setIsVinValid(false);
        setIsFormRevealed(false);
      } else {
        setShowManualVin(false);
        setFormData((p) => ({ ...p, vin: value }));
        setIsVinValid(false);
        setIsFormRevealed(false);
      }
    } else if (["mobile", "kilometer", "platePart1", "platePart2", "plateCityCode", "nationalCode"].includes(name)) {
      const finalVal = normalized.replace(/[^0-9]/g, "");
      setFormData((p) => ({ ...p, [name]: finalVal }));
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }

    if (name === "nationalCode") {
      setIsFormRevealed(false);
      const finalVal = normalized.replace(/[^0-9]/g, "");
      if (finalVal.length === 10) {
        fetchCarsForNationalId(finalVal);
      }
    }
  };

  const handleValidateVin = async () => {
    if (!formData.vin || formData.vin.length !== 17) {
      Swal.fire({ title: "خطا", text: "شماره شاسی باید ۱۷ کاراکتر باشد.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return;
    }

    setIsValidatingVin(true);
    try {
      const res = await fetch(`${BOOKING_BASE}Booking/IsValidVinnumber?VinNumber=${formData.vin}`);
      const isValid = await res.json();

      if (isValid === true) {
        const bzCheck = verifyBzOffline(formData.vin);
        if (bzCheck.match) {
          Swal.fire({
            title: "خطا",
            html: 'با توجه به پروتکل های ایمنی خودرو های برقی، سرویس در محل برای خودرو های BZ3 و BZ4 میسر نمی باشد.<br/> خواهشمند است برای رزرو نوبت به <a href="/repairshopReserve" target="_blank" class="text-[#2b2b2b] hover:underline" style="color: #2b2b2b; text-decoration: underline;">نوبت دهی اینترنتی تعمیرگاه</a> مراجعه نمایید.',
            icon: "error",
            confirmButtonText: "تایید",
            confirmButtonColor: "#1e3a8a"
          });
          setIsVinValid(false);
          return;
        }

        // Check for existing open booking
        try {
          const reserveRes = await fetch(`${BOOKING_BASE}Booking/CheckExistsReserveVinNumber?VinNumber=${formData.vin}`);
          if (reserveRes.ok) {
            const hasExisting = await reserveRes.json();
            if (hasExisting === true || (typeof hasExisting === 'object' && Object.values(hasExisting).includes(true))) {
              Swal.fire({
                title: "نوبت فعال",
                text: "برای این شماره شاسی یک نوبت فعال (باز) وجود دارد. امکان ثبت نوبت جدید تا تعیین تکلیف نوبت قبلی وجود ندارد.",
                icon: "warning",
                confirmButtonText: "تایید",
                confirmButtonColor: "#1e3a8a"
              });
              setIsVinValid(false);
              return;
            }
          }
        } catch (e) {
          console.error("Error checking existing reserve:", e);
        }

        // Fetch user data from allocated cars
        try {
          const resInfo = await fetch(`${BOOKING_BASE}Booking/getSubscriberChassisAllocation?VinNumber=${formData.vin}`);
          if (resInfo.ok) {
            const text = await resInfo.text();
            if (text) {
              const info = JSON.parse(text);
              if (info.SubscriberNationalCode && info.SubscriberNationalCode === formData.nationalCode) {
                const updates = {};
                if (info.SubscriberFirstName) updates.firstName = info.SubscriberFirstName;
                if (info.SubscriberLastName) updates.lastName = info.SubscriberLastName;
                if (info.SubscriberMobile) updates.mobile = info.SubscriberMobile;

                const plateLetterMap = {
                  1: "الف", 2: "ب", 3: "پ", 4: "ت", 5: "ث", 6: "ج", 7: "چ", 8: "ح", 9: "خ", 10: "د",
                  11: "ذ", 12: "ر", 13: "ز", 14: "ژ", 15: "س", 16: "ش", 17: "ص", 18: "ض", 19: "ط", 20: "ظ",
                  21: "ع", 22: "غ", 23: "ف", 24: "ق", 25: "ک", 26: "گ", 27: "ل", 28: "م", 29: "ن", 30: "و",
                  31: "ه", 32: "ی"
                };

                if (info.ChassisPlateVm) {
                  updates.platePart1 = String(info.ChassisPlateVm['<PartOne>k__BackingField'] || '');
                  const letterCode = info.ChassisPlateVm['<PartTwo>k__BackingField'];
                  updates.plateLetter = plateLetterMap[letterCode] || '';
                  updates.platePart2 = String(info.ChassisPlateVm['<PartThree>k__BackingField'] || '');
                  updates.plateCityCode = String(info.ChassisPlateVm['<PartFour>k__BackingField'] || '');
                }

                if (Object.keys(updates).length > 0) {
                  setFormData((p) => ({ ...p, ...updates }));
                  setIsFormRevealed(true);
                  Swal.fire({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 4000,
                    icon: 'success',
                    title: 'اطلاعات شما با موفقیت تطبیق داده شد و فرم تکمیل گردید.'
                  });
                } else {
                  setIsFormRevealed(true);
                }
              } else {
                setIsFormRevealed(true);
                Swal.fire({
                  toast: true,
                  position: 'top-end',
                  showConfirmButton: false,
                  timer: 5000,
                  icon: 'info',
                  title: 'کدملی با مالک خودرو تطابق ندارد. لطفا فرم را به صورت دستی تکمیل کنید.'
                });
              }
            } else {
              setIsFormRevealed(true);
            }
          } else {
            setIsFormRevealed(true);
          }
        } catch (e) {
          console.error("Could not fetch personal info", e);
          setIsFormRevealed(true);
        }

        setIsVinValid(true);
      } else {
        Swal.fire({
          title: "خطا",
          text: "خودروی اعلامی در سیستم برساوش ثبت نشده است در صورت اطمینان از متعلق بودن خودرو به برساوش با همکاران ما تماس حاصل نمایید",
          icon: "error",
          confirmButtonText: "تایید",
          confirmButtonColor: "#1e3a8a"
        });
        setIsVinValid(false);
      }
    } catch (e) {
      console.error(e);
      Swal.fire({ title: "خطا!", text: "خطا در ارتباط با سرور", icon: "error", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
    } finally {
      setIsValidatingVin(false);
    }
  };



  const validateForm = () => {
    const { firstName, lastName, mobile, vin, kilometer, platePart1, plateLetter, platePart2, plateCityCode } = formData;

    if (!dummyProvince) {
      Swal.fire({ title: "خطا", text: "لطفاً استان جهت دریافت سرویس را انتخاب کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }

    if (!firstName.trim() || !lastName.trim()) {
      Swal.fire({ title: "خطا", text: "لطفاً نام و نام خانوادگی را وارد کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    if (!mobile.startsWith("09") || mobile.length !== 11) {
      Swal.fire({ title: "خطا", text: "شماره موبایل باید ۱۱ رقم باشد و با ۰۹ شروع شود.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    if (!isVinValid) {
      Swal.fire({ title: "خطا", text: "لطفاً ابتدا شماره VIN خودرو را بررسی و تأیید کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    if (!platePart1 || !plateLetter || !platePart2 || !plateCityCode) {
      Swal.fire({ title: "خطا", text: "لطفاً پلاک خودرو را به صورت کامل وارد کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    if (!kilometer.trim()) {
      Swal.fire({ title: "خطا", text: "لطفاً کارکرد خودرو را وارد کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    if (!selectedDate || !selectedTime) {
      Swal.fire({ title: "خطا", text: "لطفاً زمان مورد نظر را انتخاب کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    if (!location) {
      Swal.fire({ title: "خطا", text: "لطفاً موقعیت مکانی خود را روی نقشه مشخص کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    if (!formData.address || !formData.address.trim()) {
      Swal.fire({ title: "خطا", text: "لطفاً آدرس دقیق خود را وارد کنید.", icon: "warning", confirmButtonText: "تایید", confirmButtonColor: "#1e3a8a" });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const payload = {
      // Hardcoded fields
      SubCountryId: HARDCODED_PROVINCE.UniqueId,
      SubCountryLocalizedName: HARDCODED_PROVINCE.SubCountryLocalizedName,
      CityId: HARDCODED_CITY.UniqueId,
      CityLocalizedName: HARDCODED_CITY.CityLocalizedName,
      DealerId: HARDCODED_DEALER.UniqueId,
      DealerName: HARDCODED_DEALER.DealerName,
      BranchId: HARDCODED_BRANCH.UniqueId,
      BranchName: HARDCODED_BRANCH.BranchName,
      ServerGroupId: HARDCODED_SERVER_GROUP.UniqueId,
      ServerGroupName: HARDCODED_SERVER_GROUP.ServerGroupName,
      ServerGroupLocalizedName: HARDCODED_SERVER_GROUP.ServerGroupLocalizedName,
      // User-provided fields
      BookingFirstName: formData.firstName,
      BookingLastName: formData.lastName,
      BookingVinNumber: formData.vin,
      BookingVehicleModelId: formData.vehicleModelId || "79abb1a5-5e46-420e-b058-0fc9b4a4cd4e",
      BookingKilometer: parseInt(formData.kilometer, 10) || 0,
      WorkShopTimeTableId: selectedDate?.Value || null,
      BookingServerGroupId: HARDCODED_SERVER_GROUP.UniqueId,
      BookingTime: selectedTime?.Value || selectedTime?.Text || null,
      BookingRepairAdviserId: "2f8b6554-1ad4-48c4-aebe-c76b79d9f2e8",
      BookingMobile: formData.mobile,
      BookingChassisPlate: {
        PartOne: parseInt(formData.platePart1, 10) || 0,
        PartTwo: formData.plateLetter,
        PartThree: parseInt(formData.platePart2, 10) || 0,
        PartFour: parseInt(formData.plateCityCode, 10) || 0,
        PartFive: 0,
        PartSix: 0,
        FullNumberPlate: `${formData.platePart1}${formData.plateLetter}${formData.platePart2}-${formData.plateCityCode}`,
        VehicleUsageType: "Car"
      },
      FullAddress: formData.address,
      Latitude: location?.lat || null,
      Longitude: location?.lng || null,
    };

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const kmRes = await fetch(`${BOOKING_BASE}Booking/CheckIsValidKilometer?VinNumber=${formData.vin}&Kilometer=${formData.kilometer}`);
      if (kmRes.ok) {
        const kmText = await kmRes.text();
        if (kmText === "false" || kmText === false) {
          Swal.fire({
            title: "خطا",
            text: "کیلومتر وارد شده از کیلومتر ثبت شده در سیستم کمتر می باشد ، لطفا اطلاعات را با دقت وارد نمایید.",
            icon: "error",
            confirmButtonText: "تایید",
            confirmButtonColor: "#1e3a8a"
          });
          setIsSubmitting(false);
          return;
        }
      }
    } catch (err) {
      console.error("Error validating kilometer:", err);
    }

    console.log("Submitting home service request with payload:", payload);

    try {
      const res = await fetch(`${BOOKING_BASE}Booking/InsertBooking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseText = await res.text();
      let responseData = responseText;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) { }

      console.log("Server response status:", res.status);
      console.log("Server response data:", responseData);

      if (!res.ok) {
        throw new Error(responseData?.message || `خطای سرور: ${res.status}`);
      }

      const trackingCode = responseData?.ReturnModel?.TrackingCode || responseData?.ReturnKey || "---";
      const smsText = `مشتری گرامی، نوبت سرویس در محل شما برای تاریخ ${selectedDate?.Text || ""} ساعت ${selectedTime?.Text || ""} با کد پیگیری ${trackingCode} ثبت گردید؛ جهت هماهنگی با شما تماس گرفته خواهد شد.\nبا تشکر شرکت برساوش تجارت نیک`;

      try {
        fetch("/api/sms/send_sms/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: formData.mobile,
            message: smsText
          })
        }).catch(err => console.error("SMS fetch error:", err));
      } catch (smsError) {
        console.error("SMS sending failed:", smsError);
      }

      await Swal.fire({
        title: "نوبت شما ثبت شد ✅",
        html: `<div dir="rtl" style="text-align:right">
          <div style="background-color: #e5e7eb; border: 1px solid #d1d5db; padding: 12px; border-radius: 12px; margin-bottom: 12px; text-align: center;">
            <h3 style="color: #111827; font-size: 16px; font-weight: bold; margin: 0;">کد پیگیری: <span style="font-family: monospace; background: white; padding: 2px 6px; border-radius: 4px; border: 1px solid #d1d5db; display: inline-block;" dir="ltr">${trackingCode}</span></h3>
          </div>
          <p>نوبت سرویس در محل با موفقیت ثبت شد.</p>
          <p style="margin-top:8px; color:#6b7280; font-size:14px;">کارشناسان ما در زمان مقرر با شما تماس خواهند گرفت.</p>
        </div>`,
        icon: "success",
        confirmButtonText: "تأیید",
        confirmButtonColor: "#1e3a8a",
      });
      // Reset
      setFormData({ firstName: "", lastName: "", mobile: "", vin: "", kilometer: "", platePart1: "", plateLetter: "", platePart2: "", plateCityCode: "", nationalCode: "", address: "", vehicleModelId: "" });
      setIsVinValid(false);
      setIsFormRevealed(false);
      setSelectedDate(null);
      setSelectedTime(null);
      setLocation(null);
      setDummyProvince(null);
    } catch (err) {
      Swal.fire({ title: "خطا!", text: err.message || "ارتباط با سرور برقرار نشد.", icon: "error", confirmButtonText: "متوجه شدم", confirmButtonColor: "#1e3a8a" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div dir="rtl" className="w-full  mx-auto ">
      {/* Card */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="px-5 py-6 sm:px-7 flex flex-col gap-8">

          {/* ── Personal Info ── */}
          <div className="flex flex-col gap-4">
            {/* Verification Check */}
            <div className="bg-gray-50/50 p-4 sm:p-6 rounded-2xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-5">
                <span className="bg-[#2b2b2b] text-white p-1.5 rounded-lg"><Car size={16} /></span>
                استعلام و تکمیل خودکار اطلاعات
              </div>
              <div className="flex flex-col gap-5 w-full">

                {/* National Code Row */}
                <div className="w-full space-y-2">
                  <label className="text-xs font-semibold text-slate-600">کدملی مالک <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      name="nationalCode"
                      value={formData.nationalCode}
                      onChange={handleInputChange}
                      dir="ltr"
                      maxLength={10}
                      placeholder="۱۰ رقم"
                      className="w-full px-4 py-3.5 rounded-xl border-2 font-bold text-sm text-right bg-white border-slate-200 focus:border-blue-500 outline-none transition-all"
                    />
                    {isFetchingCars && (
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        <Loader2 size={20} className="animate-spin text-[#2b2b2b]" />
                      </div>
                    )}
                  </div>
                </div>

                {/* VIN Row (Appears after National ID is entered) */}
                {formData.nationalCode.length === 10 && (
                  <div className="w-full space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                    <label className="text-xs font-semibold text-slate-600">شماره شاسی (VIN) <span className="text-red-500">*</span></label>

                    {allocatedCars.length > 0 && !showManualVin ? (
                      <select
                        name="vinSelect"
                        value={formData.vin || ""}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3.5 rounded-xl border-2 font-bold text-sm bg-white border-slate-200 focus:border-blue-500 outline-none transition-all cursor-pointer text-right"
                      >
                        <option value="">انتخاب خودرو...</option>
                        {allocatedCars.map((car, idx) => (
                          <option key={car.VinNumber + idx} value={car.VinNumber}>{car.VinNumber} - {car.Model || 'نامشخص'}</option>
                        ))}
                        <option value="manual">+ سایر (وارد کردن دستی شماره شاسی)</option>
                      </select>
                    ) : (
                      <input
                        name="vin"
                        value={formData.vin}
                        onChange={handleInputChange}
                        placeholder="شماره ۱۷ رقمی شاسی"
                        dir="ltr"
                        maxLength={17}
                        className="w-full px-4 py-3.5 rounded-xl border-2 uppercase font-bold text-sm text-right bg-white border-slate-200 focus:border-blue-500 outline-none transition-all"
                      />
                    )}
                  </div>
                )}

                {/* Validate Button Row (Appears after VIN is entered) */}
                {formData.nationalCode.length === 10 && formData.vin.length === 17 && (
                  <div className="w-full pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                    {isVinValid && isFormRevealed ? (
                      <div className="flex items-center justify-center gap-2 px-6 py-4 bg-green-100 text-green-700 border border-green-200 rounded-xl font-bold w-full">
                        <CheckCircle2 size={20} />
                        شاسی و کدملی با موفقیت تایید شد
                      </div>
                    ) : (
                      <button
                        onClick={handleValidateVin}
                        disabled={isValidatingVin}
                        className="!flex btn-large btn-theme-primary w-full disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:scale-100 disabled:cursor-not-allowed text-lg p-4"
                      >
                        {isValidatingVin && <Loader2 size={18} className="animate-spin" />}
                        بررسی و ادامه
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {isFormRevealed && (
              <div className="space-y-6 mt-4 animate-in fade-in slide-in-from-top-4 duration-500">

                {/* ── Dummy Province Selection ── */}
                <div className="flex flex-col gap-3 bg-gray-50/50 p-4 rounded-2xl border border-gray-200 shadow-sm">
                  <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <MapPin size={16} className="text-[#2b2b2b]" />
                    امکان سرویس در محل در استان های: <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {["تهران", "البرز"].map((prov) => {
                      const isSelected = dummyProvince === prov;
                      return (
                        <button
                          key={prov}
                          type="button"
                          onClick={() => setDummyProvince(prov)}
                          className={`btn-small w-full ${isSelected ? "btn-theme-active border border-[#2b2b2b]" : "btn-theme-ghost border border-gray-200"}`}
                        >
                          {prov}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormInput
                    label="نام"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    icon={<User size={15} />}
                    placeholder="نام خود را وارد کنید"
                    required
                  />
                  <FormInput
                    label="نام خانوادگی"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    icon={<User size={15} />}
                    placeholder="نام خانوادگی"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormInput
                    label="شماره موبایل"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    icon={<Phone size={15} />}
                    placeholder="09XXXXXXXXX"
                    required
                    inputMode="numeric"
                    maxLength={11}
                  />

                  <FormInput
                    label="کارکرد خودرو (کیلومتر)"
                    name="kilometer"
                    value={formData.kilometer}
                    onChange={handleInputChange}
                    icon={<Wrench size={15} />}
                    placeholder="مثلا: 25000"
                    required
                    inputMode="numeric"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <PlateInput formData={formData} handleInputChange={handleInputChange} />
                </div>
              </div>
            )}
          </div>

          {/* ── Date & Time Selection (Visible when VIN is valid) ── */}
          {isFormRevealed && isVinValid && (
            <div className="flex flex-col gap-5 transition-all duration-500 animate-in fade-in slide-in-from-top-4">
              <h3 className="text-base font-bold text-slate-800 border-b border-slate-100 pb-2 flex items-center gap-2">
                <Calendar size={18} className="text-[#2b2b2b]" />
                انتخاب زمان سرویس
              </h3>

              <div>
                {loadingDates ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
                    <Loader2 size={20} className="animate-spin text-[#2b2b2b]" />
                    <span className="text-sm">در حال دریافت تاریخ‌های موجود...</span>
                  </div>
                ) : availableDates.length === 0 ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-slate-400 bg-slate-50 rounded-xl">
                    <AlertCircle size={18} />
                    <span className="text-sm">تاریخ موجودی یافت نشد</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {availableDates.map((d, i) => {
                      const isSelected = selectedDate?.Value === d.Value;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSelectedDate(d)}
                          className={`btn-small w-full ${isSelected ? "btn-theme-active border border-[#2b2b2b]" : "btn-theme-ghost border border-gray-200"}`}
                        >
                          {d.Text}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className={`transition-all duration-300 mt-2 ${!selectedDate ? "opacity-50 pointer-events-none grayscale" : ""}`}>
                <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
                  <span className="text-[#2b2b2b]"><Clock size={15} /></span>
                  انتخاب ساعت
                </h4>

                {loadingTimes ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-slate-500">
                    <Loader2 size={18} className="animate-spin text-[#2b2b2b]" />
                    <span className="text-sm">در حال دریافت ساعات موجود...</span>
                  </div>
                ) : availableTimes.length === 0 ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-slate-400 bg-slate-50 rounded-xl">
                    <AlertCircle size={18} />
                    <span className="text-sm">ساعتی برای این تاریخ موجود نیست</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {availableTimes.map((t, i) => {
                      const isSelected = selectedTime?.Value === t.Value;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSelectedTime(t)}
                          className={`btn-small w-full ${isSelected ? "btn-theme-active border border-[#2b2b2b]" : "btn-theme-ghost border border-gray-200"}`}
                        >
                          {t.Text}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Location Selection (Visible when form is revealed) ── */}
          {isFormRevealed && (
            <div className="flex flex-col gap-4 transition-all duration-500 animate-in fade-in slide-in-from-top-4 mt-2">
              <h3 className="text-base font-bold text-slate-800 border-b border-slate-100 pb-2 flex items-center gap-2">
                <MapPin size={18} className="text-[#2b2b2b]" />
                انتخاب لوکیشن
              </h3>
              <p className="text-xs text-slate-500 font-medium -mt-2">
                لطفاً موقعیت مکانی دقیق خودرو را روی نقشه انتخاب کنید.
              </p>

              <MapPicker onLocationSelect={setLocation} />

              {location && (
                <div className="flex items-center gap-2 text-sm text-green-600 font-semibold bg-green-50 p-3 rounded-xl">
                  <CheckCircle2 size={16} />
                  <span>موقعیت انتخاب شد</span>
                </div>
              )}

              <div className="flex flex-col gap-2 mt-2">
                <label className="text-sm font-semibold text-slate-700">
                  آدرس دقیق (خیابان، پلاک، واحد) <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="خیابان، کوچه، پلاک، واحد و ..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 text-sm bg-white border-slate-200 focus:border-blue-500 outline-none resize-none transition-all"
                />
              </div>
            </div>
          )}

          {/* ── Submit Section ── */}
          {isFormRevealed && isVinValid && (
            <div className="mt-4 animate-in fade-in slide-in-from-top-4 duration-500">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !location}
                className="max-w-2xl mx-auto !flex btn-large btn-theme-primary w-full disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:scale-100 disabled:cursor-not-allowed text-lg p-4"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    در حال ثبت...
                  </>
                ) : (
                  <>
                    ثبت نهایی نوبت
                    <Send size={20} />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[#2b2b2b] shrink-0">{icon}</span>
      <span className="text-xs text-slate-500 w-20 shrink-0">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{value}</span>
    </div>
  );
}
